import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import throttle from 'lodash/throttle';

const DragScrollWrapper = forwardRef((props, ref) => {
    const {
        children,
        className = '',
        handleOnScroll,
        horizontal = true,
        vertical = false,
        noChildDrag = false,
        scrollThrottleMs = 100,
    } = props;
    const containerRef = useRef(null);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const scrollTopRef = useRef(0);

    useImperativeHandle(ref, () => containerRef.current);

    const throttledOnScroll = useMemo(() => {
        if (!handleOnScroll) {
            return undefined;
        }

        return throttle(handleOnScroll, scrollThrottleMs);
    }, [handleOnScroll, scrollThrottleMs]);

    useEffect(() => () => {
        throttledOnScroll?.cancel();
    }, [throttledOnScroll]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        const handleMouseDown = (event) => {
            if (noChildDrag && event.target !== container) {
                return;
            }
            isDraggingRef.current = true;
            startXRef.current = event.pageX - container.offsetLeft;
            startYRef.current = event.pageY - container.offsetTop;
            scrollLeftRef.current = container.scrollLeft;
            scrollTopRef.current = container.scrollTop;
            event.preventDefault();
        };
        const handleMouseUp = () => {
            isDraggingRef.current = false;
        };
        const handleMouseMove = (event) => {
            if (!isDraggingRef.current) return;
            event.preventDefault();
            const x = event.pageX - container.offsetLeft;
            const y = event.pageY - container.offsetTop;
            const walkX = x - startXRef.current;
            const walkY = y - startYRef.current;
            if (horizontal) {
                container.scrollLeft = scrollLeftRef.current - walkX;
            }
            if (vertical) {
                container.scrollTop = scrollTopRef.current - walkY;
            }
        };
        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mouseleave', handleMouseUp);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mousemove', handleMouseMove);

        return () => {
            container.removeEventListener('mousedown', handleMouseDown);
            container.removeEventListener('mouseleave', handleMouseUp);
            container.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('mousemove', handleMouseMove);
        };
    }, [horizontal, vertical, noChildDrag]);

    return (
        <div
            ref={containerRef}
            className={className}
            onScroll={throttledOnScroll}
        >
            {children}
        </div>
    );
});

DragScrollWrapper.displayName = 'DragScrollWrapper';
export default DragScrollWrapper;
